import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  enumOptionsAt,
  isRecord,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  deleteAtPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

const REPEAT_DAYS = [
  { value: 0, label: "周日" },
  { value: 1, label: "周一" },
  { value: 2, label: "周二" },
  { value: 3, label: "周三" },
  { value: 4, label: "周四" },
  { value: 5, label: "周五" },
  { value: 6, label: "周六" },
] as const;

const KIND_LABELS = { wake: "睡眠与起床", other: "其他" };
const SOUND_LABELS = {
  Radar: "Radar",
  Apex: "Apex",
  Beacon: "Beacon",
  None: "无",
};

export function ClockPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const path = "device.apps.clock.alarms";
  const alarms = asArray(getAtPath(root, path));
  const hasClockBag = isRecord(getAtPath(root, "device.apps.clock"));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(alarms.length);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        后台数据会成为手机端的初始闹钟。玩家在手机内的增删改只保存在本地；后台数据变化后会重新初始化。
      </p>
      <ArrayShell
        title="闹钟"
        count={alarms.length}
        disabled={disabled}
        emptyText={
          hasClockBag
            ? "闹钟列表为空，手机端不会显示闹钟。"
            : "尚未配置，手机端使用内置默认闹钟。点击添加后改用后台数据。"
        }
        onAdd={() => {
          onChangeRoot(
            appendAtPath(root, path, {
              time: "07:00",
              label: "",
              enabled: true,
              kind: "other",
              sound: "Radar",
            }),
          );
          setOpenIndex(alarms.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {alarms.map((alarm, index) => {
            const record = isRecord(alarm) ? alarm : {};
            const itemPath = childPath(path, index);
            const repeatDays = asArray(record.repeat_days)
              .filter((day): day is number =>
                typeof day === "number" &&
                Number.isInteger(day) &&
                day >= 0 &&
                day <= 6,
              );
            const title = [str(record.time), str(record.label)]
              .filter(Boolean)
              .join(" · ") || `闹钟 ${index + 1}`;

            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={alarms.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <Field>
                  <FieldLabel htmlFor={`${itemPath}-time`}>时间</FieldLabel>
                  <Input
                    id={`${itemPath}-time`}
                    type="time"
                    value={str(record.time)}
                    disabled={disabled}
                    onChange={(event) =>
                      onChangeRoot(
                        setAtPath(
                          root,
                          childPath(itemPath, "time"),
                          event.target.value,
                        ),
                      )
                    }
                  />
                </Field>
                <TextField
                  label="标签"
                  value={str(record.label)}
                  disabled={disabled}
                  placeholder="按当前语言填写手机端显示文字"
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "label"), value),
                    )
                  }
                />
                <BoolField
                  label="启用"
                  value={bool(record.enabled)}
                  disabled={disabled}
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "enabled"), value),
                    )
                  }
                />
                <EnumSelect
                  label="类型"
                  value={str(record.kind, "other")}
                  options={enumOptionsAt(`${itemPath}.kind`)}
                  optionLabels={KIND_LABELS}
                  disabled={disabled}
                  onChange={(value) => {
                    if (value !== "wake") {
                      onChangeRoot(
                        setAtPath(root, childPath(itemPath, "kind"), value),
                      );
                      return;
                    }
                    const next = alarms.map((item, itemIndex) => {
                      const current = isRecord(item) ? item : {};
                      if (itemIndex === index) return { ...current, kind: "wake" };
                      return current.kind === "wake"
                        ? { ...current, kind: "other" }
                        : item;
                    });
                    onChangeRoot(setAtPath(root, path, next));
                  }}
                />
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-medium">重复星期</p>
                  <div
                    className="flex flex-wrap gap-1.5"
                    role="group"
                    aria-label="重复星期"
                  >
                    {REPEAT_DAYS.map((day) => {
                      const selected = repeatDays.includes(day.value);
                      return (
                        <Button
                          key={day.value}
                          type="button"
                          size="xs"
                          variant={selected ? "secondary" : "outline"}
                          disabled={disabled}
                          aria-pressed={selected}
                          onClick={() => {
                            const next = selected
                              ? repeatDays.filter((value) => value !== day.value)
                              : [...repeatDays, day.value].sort((a, b) => a - b);
                            const repeatPath = childPath(itemPath, "repeat_days");
                            onChangeRoot(
                              next.length > 0
                                ? setAtPath(root, repeatPath, next)
                                : deleteAtPath(root, repeatPath),
                            );
                          }}
                        >
                          {day.label}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-[0.65rem] text-muted-foreground">
                    全不选为仅响一次。
                  </p>
                </div>
                <EnumSelect
                  label="铃声"
                  value={str(record.sound, "Radar")}
                  options={enumOptionsAt(`${itemPath}.sound`)}
                  optionLabels={SOUND_LABELS}
                  disabled={disabled}
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "sound"), value),
                    )
                  }
                />
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}
